/**
 * Cadastro em 3 etapas (out/2026, pedido do Bruno): Plano → Seus dados → Acesso.
 *
 * - Plano: vem do site pela URL (`/cadastrar?plano=business&ucs_b=2&ucs_a=1`,
 *   UCs = TOTAL de unidades por grupo que o cliente vai cadastrar), fallback
 *   Free. O cliente pode trocar o plano e as UCs; o valor mensal é calculado na
 *   hora pelo espelho `utils/planRules::computeMonthlyEstimate` (o backend
 *   recalcula no envio). Plano pago pede a forma de pagamento (PIX, boleto;
 *   cartão "em breve") — a cobrança é POSTECIPADA, a 1ª sai um mês depois do
 *   cadastro. O Contrato de Adesão aparece de novo nesta etapa, numa caixa
 *   rolável junto do aceite, para o cliente não alegar desconhecimento.
 * - Seus dados: PF/PJ. PJ pede CNPJ, razão social, cargo (lista fechada,
 *   `utils/jobTitles`), e-mail de cobrança e endereço; PF pede CPF e, em plano
 *   pago, o endereço (tomador da NF). PF no Free informa o endereço no 1º
 *   serviço adicional (`BillingAddressPrompt`).
 * - Acesso: e-mail, senha, Termos de Uso e Privacidade (LGPD) e os botões
 *   sociais, que levam o plano e os dados das etapas anteriores.
 *
 * As regras de verdade (documento válido e único, endereço/pagamento
 * obrigatórios, UCs pelo plano) estão no backend (`SignupProfileService`); um
 * erro de campo devolve o usuário para a etapa do campo.
 *
 * Google OAuth é montado condicionalmente — ver armadilha no CLAUDE.md (hook
 * dentro de componente filho).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Row, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import { LoginSocialFacebook } from 'reactjs-social-login';
import { FaFacebookF } from 'react-icons/fa';
import {
  BsCheckCircleFill,
  BsReceiptCutoff,
  BsXCircleFill,
} from 'react-icons/bs';
import { useTheme } from 'styled-components';

import { FormGroup, Input, Label } from 'ui';

import { useToggle } from 'hooks/useToggle';

import { ModalUseTerms } from 'components/modal-use-terms/modal-use-terms.component';
import { ModalPrivacyPolicy } from 'components/modal-privacy-policy/modal-privacy-policy.component';
import {
  AdhesionContractText,
  ModalAdhesionContract,
} from 'components/modal-adhesion-contract/modal-adhesion-contract.component';
import {
  BillingAddressFields,
  BillingAddressValue,
  emptyBillingAddress,
  isBillingAddressComplete,
} from 'components/billing-info-form/billing-info-form.component';
import { TermInfo } from 'components/term-info/term-info.component';
import { JobTitleSelect } from 'components/job-title-select/job-title-select.component';

import logoImg from 'assets/logos/logo.png';
import googleImg from 'assets/logos/social-auth/google.png';

import { alert } from 'lib/alert';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { signUp, socialSignUp } from 'store/user/user.action';

import { ERoutes } from 'infra/routes/routes.enum';
import { FormError } from 'entities/Errors';
import { SignupProfileProps } from 'entities/User';
import { userRefresh } from 'store/user/user.slice';
import * as selectUser from 'store/user/user.selector';

import {
  PLAN_RULES_LIST,
  PlanRuleInfo,
  computeMonthlyEstimate,
} from 'utils/planRules';
import { documentNumberMask, phoneMask } from 'utils/masks';
import { sanitizeOnlyNumbers } from 'utils/sanitize';
import { format } from 'utils/format';

import {
  Footer,
  LogoImg,
  Link,
  Container,
  Header,
  Button,
  Content,
  WarnIcon,
  Body,
  GoogleButton,
  SocialSignUpContainer,
  SocialLogoImg,
  SocialOrContainer,
  FacebookButton,
  PasswordRules,
  PasswordRuleItem,
} from './signup.styles';
import {
  Stepper,
  StepItem,
  SectionTitle,
  CounterRow,
  Counter,
  SummaryBar,
  EstimateWrapper,
  PaymentGrid,
  PaymentOption,
  ContractBox,
  ButtonsRow,
  SecondaryButton,
  Hint,
  FieldError,
} from './signup-steps.styles';
import {
  PlansGrid,
  PlanCard,
  PlanCardHeader,
  PlanCardPrice,
  PlanCardFeatures,
  PlanCardFooter,
  PlanCardButton,
  RecommendedTag,
  InvoiceEstimateCard,
  InvoiceEstimateHeader,
  InvoiceEstimateBody,
  InvoiceEstimateLine,
  InvoiceEstimateDivider,
  InvoiceEstimateTotal,
} from 'pages/client/account/account.styles';
import {
  SliderLabel,
  SliderToggle,
} from 'pages/client/update-user/update-user.styles';

type Step = 'plan' | 'data' | 'access';

const STEPS: { id: Step; label: string }[] = [
  { id: 'plan', label: 'Plano' },
  { id: 'data', label: 'Seus dados' },
  { id: 'access', label: 'Acesso' },
];

// Campo do backend → etapa onde ele é preenchido
const STEP_OF_FIELD: Record<string, Step> = {
  planName: 'plan',
  declaredGroupACount: 'plan',
  declaredGroupBCount: 'plan',
  paymentMethod: 'plan',
  acceptedAdhesion: 'plan',
  name: 'data',
  documentNumber: 'data',
  companyName: 'data',
  role: 'data',
  invoiceEmail: 'data',
  phone: 'data',
  whatsappNumber: 'data',
  billingAddress: 'data',
  billingZipCode: 'data',
  billingCity: 'data',
  billingState: 'data',
};

interface ConsentState {
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  acceptedAdhesion: boolean;
}

type PaymentMethod = 'pix' | 'boleto';

const CONSENT_REQUIRED_MSG =
  'Para criar a conta é necessário aceitar os Termos de Uso, a Política de Privacidade e o Contrato de Adesão.';

// Regra simples de formato (sem espaços, um único @, domínio com pelo menos um ponto) —
// a validação definitiva de existência acontece na confirmação por e-mail após o cadastro.
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_INVALID_MSG =
  'Informe um e-mail válido, no formato nome@dominio.com.';

// Regras de senha — mesmas do backend (CreateUserValidation): mínimo de 8
// caracteres, com pelo menos uma letra e um número. As mensagens abaixo são
// exibidas na tela para o usuário saber os requisitos antes de errar.
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_RULES: { test: (value: string) => boolean; label: string }[] = [
  {
    test: (v) => v.length >= PASSWORD_MIN_LENGTH,
    label: `No mínimo ${PASSWORD_MIN_LENGTH} caracteres`,
  },
  { test: (v) => /[A-Za-z]/.test(v), label: 'Pelo menos uma letra' },
  { test: (v) => /\d/.test(v), label: 'Pelo menos um número' },
];
const isPasswordValid = (value: string): boolean =>
  PASSWORD_RULES.every((rule) => rule.test(value));
const PASSWORD_INVALID_MSG = 'A senha não atende aos requisitos mínimos.';
const PASSWORD_MISMATCH_MSG = 'As senhas não coincidem.';

const FREE_PLAN = PLAN_RULES_LIST[0];

/** Plano da URL do site (nome em qualquer caixa), senão Free. */
const planFromParam = (value: string | null): PlanRuleInfo =>
  PLAN_RULES_LIST.find(
    (p) => p.name.toLowerCase() === (value ?? '').trim().toLowerCase()
  ) ?? FREE_PLAN;

const countFromParam = (value: string | null, fallback: number): number => {
  const n = Math.trunc(Number(value));
  return Number.isFinite(n) && n >= 0 && value !== null && value !== ''
    ? Math.min(n, 999)
    : fallback;
};

/** UCs ajustadas às regras do plano (Free: 1 só; Starter: sem Grupo A). */
const clampUnits = (plan: PlanRuleInfo, groupB: number, groupA: number) => {
  const a = plan.allowsGroupA ? groupA : 0;
  const max = Number.isFinite(plan.maxConsumerUnits)
    ? plan.maxConsumerUnits
    : 999;
  const b = Math.min(groupB, Math.max(0, max - a));
  return { groupB: a + b === 0 ? 1 : b, groupA: a };
};

/** 1ª cobrança: aniversário de um mês (dia 31 → último dia do mês). */
const firstBillingDate = (): string => {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const lastDay = new Date(
    target.getFullYear(),
    target.getMonth() + 1,
    0
  ).getDate();
  target.setDate(Math.min(now.getDate(), lastDay));
  return target.toLocaleDateString('pt-BR');
};

const isDocumentLengthOk = (doc: string, isCompany: boolean) =>
  sanitizeOnlyNumbers(doc).length === (isCompany ? 14 : 11);

type SocialPayload = ConsentState & SignupProfileProps;

const GoogleSignUpButton = ({
  payload,
  onBlocked,
}: {
  payload: SocialPayload;
  onBlocked: () => boolean;
}) => {
  const dispatch = useAppDispatch();
  const handleGoogleSignUp = useGoogleLogin({
    onSuccess: (response) => {
      dispatch(
        socialSignUp({
          provider: 'google',
          token: response.access_token,
          ...payload,
        })
      );
    },
    onError: () => {
      alert.error(
        'Ocorreu um erro ao tentar criar conta. Tente novamente ou entre em contato com o suporte.'
      );
    },
  });
  return (
    <GoogleButton
      onClick={() => {
        if (onBlocked()) return;
        handleGoogleSignUp();
      }}
    >
      <div>
        <SocialLogoImg src={googleImg} alt="Google" />
      </div>
      <div>Criar com Google</div>
    </GoogleButton>
  );
};

interface DataForm {
  name: string;
  isCompany: boolean;
  documentNumber: string;
  companyName: string;
  role: string;
  invoiceEmail: string;
  phone: string;
  whatsappNumber: string;
}

interface AccessForm {
  email: string;
  password: string;
  confirmPassword: string;
}

const SignUp = () => {
  const [searchParams] = useSearchParams();

  const initialPlan = planFromParam(searchParams.get('plano'));
  const initialUnits = clampUnits(
    initialPlan,
    countFromParam(searchParams.get('ucs_b'), 1),
    countFromParam(searchParams.get('ucs_a'), 0)
  );

  const [step, setStep] = useState<Step>('plan');
  const [plan, setPlan] = useState<PlanRuleInfo>(initialPlan);
  const [groupB, setGroupB] = useState(initialUnits.groupB);
  const [groupA, setGroupA] = useState(initialUnits.groupA);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');

  const [data, setData] = useState<DataForm>({
    name: '',
    isCompany: false,
    documentNumber: '',
    companyName: '',
    role: '',
    invoiceEmail: '',
    phone: '',
    whatsappNumber: '',
  });
  const [address, setAddress] =
    useState<BillingAddressValue>(emptyBillingAddress);

  const [access, setAccess] = useState<AccessForm>({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [formError, setFormError] = useState<FormError>({} as FormError);

  const [showModalPrivacy, toggleModalPrivacy] = useToggle();
  const [showModalTerms, toggleModalTerms] = useToggle();
  const [showModalAdhesion, toggleModalAdhesion] = useToggle();

  // LGPD — aceites obrigatórios (enviados ao backend e registrados no banco)
  const [consents, setConsents] = useState<ConsentState>({
    acceptedTerms: false,
    acceptedPrivacy: false,
    acceptedAdhesion: false,
  });
  const consentsOk =
    consents.acceptedTerms &&
    consents.acceptedPrivacy &&
    consents.acceptedAdhesion;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const currentUser = useAppSelector(selectUser.currentUser);
  const userStatus = useAppSelector(selectUser.status);
  const userAction = useAppSelector(selectUser.action);
  const userError = useAppSelector(selectUser.error);

  const isPaid = plan.price > 0;
  const needsAddress = data.isCompany || isPaid;
  const estimate = useMemo(
    () => computeMonthlyEstimate(plan, groupB, groupA),
    [plan, groupB, groupA]
  );

  useEffect(() => {
    setFormError({});
    dispatch(userRefresh());
  }, []);

  useEffect(() => {
    if (userAction === 'signUp') {
      if (userStatus === 'successed') {
        if (!currentUser?.enabled) {
          navigate('/cadastrar/confirmar-email'); // v7: navigate() devolve Promise — não retornar do useEffect
          return;
        }
        navigate('/dashboard');
      } else if (userStatus === 'failed' && userError) {
        if (userError.type !== 'validation') {
          alert.error(userError.message);
        } else {
          const validationErrors = userError?.details?.reduce((acc, error) => {
            return { ...acc, [error.key]: error.message };
          }, {} as FormError);

          setFormError(validationErrors);
          const firstKey = userError?.details?.[0]?.key;
          if (firstKey) setStep(STEP_OF_FIELD[firstKey] ?? 'access');
        }
      }
    }
  }, [userStatus]);

  const selectPlan = (next: PlanRuleInfo) => {
    const units = clampUnits(next, groupB, groupA);
    setPlan(next);
    setGroupB(units.groupB);
    setGroupA(units.groupA);
    if (next.price === 0) setPaymentMethod('');
  };

  const changeUnits = (nextB: number, nextA: number) => {
    const units = clampUnits(plan, Math.max(0, nextB), Math.max(0, nextA));
    setGroupB(units.groupB);
    setGroupA(units.groupA);
  };

  const handleDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'phone' || name === 'whatsappNumber') {
      setData({ ...data, [name]: phoneMask(value) });
    } else if (name === 'documentNumber') {
      setData({ ...data, documentNumber: documentNumberMask(value) });
    } else {
      setData({ ...data, [name]: value });
    }
  };

  const handleAccessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAccess({ ...access, [name]: value });
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setConsents((prev) => ({ ...prev, [name]: checked }));
  };

  // ── Validação por etapa (o backend confere de novo) ───────────────────
  const validatePlan = (): FormError => {
    const errors: FormError = {};
    if (isPaid && !paymentMethod) {
      errors.paymentMethod = 'Escolha a forma de pagamento.';
    }
    if (!consents.acceptedAdhesion) {
      errors.acceptedAdhesion = 'É necessário aceitar o Contrato de Adesão.';
    }
    return errors;
  };

  const validateData = (): FormError => {
    const errors: FormError = {};
    const docLabel = data.isCompany ? 'CNPJ' : 'CPF';
    if (!data.name.trim()) errors.name = 'Informe o nome.';
    if (!isDocumentLengthOk(data.documentNumber, data.isCompany)) {
      errors.documentNumber = `Informe um ${docLabel} válido.`;
    }
    if (sanitizeOnlyNumbers(data.phone).length < 10) {
      errors.phone = 'Informe o telefone com DDD.';
    }
    if (data.isCompany) {
      if (!data.companyName.trim())
        errors.companyName = 'Informe a razão social.';
      if (!data.role) errors.role = 'Escolha o cargo.';
      if (data.invoiceEmail && !EMAIL_FORMAT_REGEX.test(data.invoiceEmail)) {
        errors.invoiceEmail = EMAIL_INVALID_MSG;
      }
    }
    if (needsAddress && !isBillingAddressComplete(address)) {
      errors.billingAddress = 'Preencha CEP, endereço, cidade e UF.';
    }
    return errors;
  };

  const goTo = (next: Step) => {
    const errors =
      step === 'plan' && next !== 'plan'
        ? validatePlan()
        : step === 'data' && next === 'access'
        ? validateData()
        : {};
    setFormError(errors);
    if (Object.keys(errors).length === 0) setStep(next);
  };

  // Dados das etapas 1 e 2 — iguais no cadastro por e-mail e no social
  const profile: SignupProfileProps = {
    planName: plan.name,
    declaredGroupACount: groupA,
    declaredGroupBCount: groupB,
    paymentMethod: isPaid ? (paymentMethod as PaymentMethod) : null,
    isCompany: data.isCompany,
    documentNumber: sanitizeOnlyNumbers(data.documentNumber),
    companyName: data.isCompany ? data.companyName.trim() : '',
    role: data.isCompany ? data.role : '',
    invoiceEmail: data.isCompany ? data.invoiceEmail.trim() : '',
    phone: sanitizeOnlyNumbers(data.phone),
    whatsappNumber: sanitizeOnlyNumbers(data.whatsappNumber),
    billingAddress: address.billingAddress.trim() || null,
    billingZipCode: sanitizeOnlyNumbers(address.billingZipCode) || null,
    billingCity: address.billingCity.trim() || null,
    billingState: address.billingState.trim().toUpperCase() || null,
  };

  // Social: só depois das 3 aceitações (etapa 1 e etapa 3)
  const socialBlocked = (): boolean => {
    if (!consentsOk) {
      alert.error(CONSENT_REQUIRED_MSG);
      return true;
    }
    return false;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!consentsOk) {
      alert.error(CONSENT_REQUIRED_MSG);
      return;
    }

    if (!EMAIL_FORMAT_REGEX.test(access.email)) {
      setFormError({ email: EMAIL_INVALID_MSG });
      return;
    }

    if (!isPasswordValid(access.password || '')) {
      setFormError({ password: PASSWORD_INVALID_MSG });
      return;
    }

    if (access.password !== access.confirmPassword) {
      setFormError({ confirmPassword: PASSWORD_MISMATCH_MSG });
      return;
    }

    dispatch(
      signUp({
        ...profile,
        name: data.name.trim(),
        email: access.email,
        password: access.password,
        token: '',
        documentNumber: profile.documentNumber ?? '',
        companyName: profile.companyName ?? '',
        invoiceEmail: profile.invoiceEmail ?? '',
        isCompany: data.isCompany,
        phone: profile.phone ?? '',
        role: profile.role ?? '',
        whatsappNumber: profile.whatsappNumber ?? '',
        ...consents,
      })
    );
    setFormError({});
  };

  const handleFacebookSingUp = (response: any) => {
    if (socialBlocked()) return;
    dispatch(
      socialSignUp({
        provider: 'facebook',
        token: response.data.accessToken,
        ...consents,
        ...profile,
      })
    );
  };

  const stepIndex = STEPS.findIndex((s) => s.id === step);

  const summary = (
    <SummaryBar>
      <div>
        <b>Plano {plan.name}</b>
        <span>
          {groupB} UC(s) baixa tensão
          {plan.allowsGroupA ? ` · ${groupA} alta tensão` : ''}
        </span>
      </div>
      <strong>
        {estimate.total > 0
          ? `R$ ${format.money(estimate.total)}/mês`
          : 'Gratuito'}
      </strong>
    </SummaryBar>
  );

  const renderPlanStep = () => (
    <>
      <SectionTitle>Escolha o seu plano</SectionTitle>
      {/* Mesmos cards de /conta (account.styles) */}
      <PlansGrid>
        {PLAN_RULES_LIST.map((p) => {
          const selected = p.name === plan.name;
          return (
            <PlanCard
              key={p.name}
              isCurrent={selected}
              recommended={p.recommended}
            >
              {p.recommended && !selected && (
                <RecommendedTag>Recomendado</RecommendedTag>
              )}
              <PlanCardHeader isCurrent={selected} recommended={p.recommended}>
                <h3>{p.name}</h3>
                <p>{p.tagline}</p>
              </PlanCardHeader>
              <PlanCardPrice>
                {p.price === 0 ? (
                  <div className="price-free">Gratuito</div>
                ) : (
                  <>
                    <span className="price-value">
                      R$ {format.money(p.price)}
                    </span>
                    <span className="price-period">/mês</span>
                  </>
                )}
              </PlanCardPrice>
              <PlanCardFeatures>
                <li>
                  <BsCheckCircleFill
                    size={14}
                    color={theme.color.ui.greenAlt}
                  />
                  {p.includedConsumerUnits} UC
                  {p.includedConsumerUnits > 1 ? 's' : ''} incluída
                  {p.includedConsumerUnits > 1 ? 's' : ''}
                </li>
                <li>
                  {p.allowsGroupA ? (
                    <BsCheckCircleFill
                      size={14}
                      color={theme.color.ui.greenAlt}
                    />
                  ) : (
                    <BsXCircleFill size={14} color={theme.color.ui.shade2} />
                  )}
                  Grupo A (média/alta tensão)
                </li>
                <li>
                  {Number.isFinite(p.maxConsumerUnits) ? (
                    <BsXCircleFill size={14} color={theme.color.ui.shade2} />
                  ) : (
                    <BsCheckCircleFill
                      size={14}
                      color={theme.color.ui.greenAlt}
                    />
                  )}
                  UC adicional{' '}
                  {p.additionalCostGroupB > 0
                    ? `a partir de R$ ${format.money(
                        p.additionalCostGroupB
                      )}/mês`
                    : '— indisponível'}
                </li>
              </PlanCardFeatures>
              <PlanCardFooter>
                <PlanCardButton
                  type="button"
                  isCurrent={selected}
                  disabled={selected}
                  onClick={() => selectPlan(p)}
                >
                  {selected ? 'Plano escolhido' : 'Escolher'}
                </PlanCardButton>
              </PlanCardFooter>
            </PlanCard>
          );
        })}
      </PlansGrid>

      <SectionTitle>
        Quantos imóveis (unidades consumidoras) você vai acompanhar?
      </SectionTitle>
      <CounterRow>
        <Counter>
          <span>
            Baixa tensão (Grupo B)
            <TermInfo term="grupoB" />
          </span>
          <div>
            <button
              type="button"
              aria-label="Menos uma UC de baixa tensão"
              onClick={() => changeUnits(groupB - 1, groupA)}
            >
              −
            </button>
            <output>{groupB}</output>
            <button
              type="button"
              aria-label="Mais uma UC de baixa tensão"
              onClick={() => changeUnits(groupB + 1, groupA)}
            >
              +
            </button>
          </div>
        </Counter>
        <Counter disabled={!plan.allowsGroupA}>
          <span>
            Alta tensão (Grupo A)
            <TermInfo text="Alta tensão (Grupo A): a conta tem demanda contratada em kW — indústrias, grandes comércios e propriedades rurais com transformador próprio." />
          </span>
          <div>
            <button
              type="button"
              aria-label="Menos uma UC de alta tensão"
              disabled={!plan.allowsGroupA}
              onClick={() => changeUnits(groupB, groupA - 1)}
            >
              −
            </button>
            <output>{groupA}</output>
            <button
              type="button"
              aria-label="Mais uma UC de alta tensão"
              disabled={!plan.allowsGroupA}
              onClick={() => changeUnits(groupB, groupA + 1)}
            >
              +
            </button>
          </div>
          {!plan.allowsGroupA && (
            <Hint>
              Alta tensão está disponível no Business e no Enterprise.
            </Hint>
          )}
        </Counter>
      </CounterRow>
      {Number.isFinite(plan.maxConsumerUnits) && (
        <Hint>
          O plano {plan.name} acompanha {plan.maxConsumerUnits} unidade
          consumidora.
        </Hint>
      )}

      {/* Mesmo card da estimativa de /conta (account.styles) */}
      <EstimateWrapper>
        <InvoiceEstimateCard>
          <InvoiceEstimateHeader>
            <BsReceiptCutoff size={16} />
            <h4>Estimativa mensal — {plan.name}</h4>
          </InvoiceEstimateHeader>
          <InvoiceEstimateBody>
            <InvoiceEstimateLine>
              <span>Plano {plan.name}</span>
              <span>
                {estimate.planPrice === 0
                  ? 'Grátis'
                  : `R$ ${format.money(estimate.planPrice)}`}
              </span>
            </InvoiceEstimateLine>
            {estimate.additionalGroupBCount > 0 && (
              <InvoiceEstimateLine>
                <span>
                  {estimate.additionalGroupBCount} UC(s) adicional(is) Grupo B ×
                  R$ {format.money(plan.additionalCostGroupB)}
                </span>
                <span>R$ {format.money(estimate.additionalGroupB)}</span>
              </InvoiceEstimateLine>
            )}
            {estimate.additionalGroupACount > 0 && (
              <InvoiceEstimateLine>
                <span>
                  {estimate.additionalGroupACount} UC(s) adicional(is) Grupo A ×
                  R$ {format.money(plan.additionalCostGroupA)}
                </span>
                <span>R$ {format.money(estimate.additionalGroupA)}</span>
              </InvoiceEstimateLine>
            )}
            <InvoiceEstimateDivider />
            <InvoiceEstimateTotal>
              <span>Total</span>
              <div className="text-end">
                <div className="total-value">
                  R$ {format.money(estimate.total)}
                </div>
                <div className="total-period">/mês</div>
              </div>
            </InvoiceEstimateTotal>
          </InvoiceEstimateBody>
        </InvoiceEstimateCard>
      </EstimateWrapper>
      <Hint>
        A mensalidade considera os imóveis que você cadastrar na plataforma.
      </Hint>

      {isPaid && (
        <>
          <SectionTitle>Forma de pagamento</SectionTitle>
          <PaymentGrid>
            <PaymentOption
              type="button"
              selected={paymentMethod === 'pix'}
              onClick={() => setPaymentMethod('pix')}
            >
              PIX
            </PaymentOption>
            <PaymentOption
              type="button"
              selected={paymentMethod === 'boleto'}
              onClick={() => setPaymentMethod('boleto')}
            >
              Boleto
            </PaymentOption>
            <PaymentOption type="button" disabled>
              Cartão de crédito <small>(em breve)</small>
            </PaymentOption>
          </PaymentGrid>
          {formError.paymentMethod && (
            <FieldError>{formError.paymentMethod}</FieldError>
          )}
          <Hint>
            Você paga ao fim do primeiro mês de uso: a 1ª cobrança sai em{' '}
            {firstBillingDate()}, com a nota fiscal.
          </Hint>
        </>
      )}

      <SectionTitle>Contrato de Adesão</SectionTitle>
      <ContractBox>
        <AdhesionContractText compact />
      </ContractBox>
      <Hint>
        <b role="button" tabIndex={0} onClick={toggleModalAdhesion}>
          Abrir em tela cheia
        </b>
      </Hint>
      <div className="form-check mt-2">
        <input
          className="form-check-input"
          type="checkbox"
          id="acceptedAdhesion"
          name="acceptedAdhesion"
          checked={consents.acceptedAdhesion}
          onChange={handleConsentChange}
        />
        <label className="form-check-label" htmlFor="acceptedAdhesion">
          Li e aceito o Contrato de Adesão ao plano <b>{plan.name}</b>
        </label>
      </div>
      {formError.acceptedAdhesion && (
        <FieldError>{formError.acceptedAdhesion}</FieldError>
      )}

      <ButtonsRow>
        <Button type="button" onClick={() => goTo('data')}>
          Continuar
        </Button>
      </ButtonsRow>
    </>
  );

  const renderDataStep = () => (
    <>
      {/* Mesmo interruptor de /atualizar-dados (update-user.styles) */}
      <div className="d-flex flex-row align-items-center mb-2">
        <SliderToggle className="switch me-3">
          <input
            type="checkbox"
            name="isCompany"
            aria-label="Pessoa jurídica"
            checked={data.isCompany}
            onChange={() => setData({ ...data, isCompany: !data.isCompany })}
          />
          <span className="slider round"></span>
        </SliderToggle>
        <SliderLabel>
          {data.isCompany ? 'Pessoa Jurídica' : 'Pessoa Física'}
        </SliderLabel>
      </div>
      <Row>
        <Col md={6}>
          <FormGroup hasError={!!formError.name}>
            <Label htmlFor="name">
              {data.isCompany ? 'Nome do responsável' : 'Nome completo'}
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              value={data.name}
              onChange={handleDataChange}
            />
            {formError.name && <span>{formError.name}</span>}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup hasError={!!formError.documentNumber}>
            <Label htmlFor="documentNumber">
              {data.isCompany ? 'CNPJ' : 'CPF'}
            </Label>
            <Input
              id="documentNumber"
              name="documentNumber"
              type="text"
              inputMode="numeric"
              maxLength={data.isCompany ? 18 : 14}
              value={data.documentNumber}
              onChange={handleDataChange}
            />
            {formError.documentNumber && (
              <span>{formError.documentNumber}</span>
            )}
          </FormGroup>
        </Col>
      </Row>
      {data.isCompany && (
        <Row>
          <Col md={6}>
            <FormGroup hasError={!!formError.companyName}>
              <Label htmlFor="companyName">Razão social</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                autoComplete="organization"
                value={data.companyName}
                onChange={handleDataChange}
              />
              {formError.companyName && <span>{formError.companyName}</span>}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup hasError={!!formError.role}>
              <Label htmlFor="role">Cargo</Label>
              <JobTitleSelect
                value={data.role}
                onChange={(value) => setData({ ...data, role: value })}
              />
              {formError.role && <span>{formError.role}</span>}
            </FormGroup>
          </Col>
        </Row>
      )}
      <Row>
        <Col md={data.isCompany ? 4 : 6}>
          <FormGroup hasError={!!formError.phone}>
            <Label htmlFor="phone">Telefone</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              value={data.phone}
              onChange={handleDataChange}
            />
            {formError.phone && <span>{formError.phone}</span>}
          </FormGroup>
        </Col>
        <Col md={data.isCompany ? 4 : 6}>
          <FormGroup hasError={!!formError.whatsappNumber}>
            <Label htmlFor="whatsappNumber">WhatsApp (opcional)</Label>
            <Input
              id="whatsappNumber"
              name="whatsappNumber"
              type="tel"
              value={data.whatsappNumber}
              onChange={handleDataChange}
            />
            {formError.whatsappNumber && (
              <span>{formError.whatsappNumber}</span>
            )}
          </FormGroup>
        </Col>
        {data.isCompany && (
          <Col md={4}>
            <FormGroup hasError={!!formError.invoiceEmail}>
              <Label htmlFor="invoiceEmail">E-mail de cobrança</Label>
              <Input
                id="invoiceEmail"
                name="invoiceEmail"
                type="email"
                placeholder="o mesmo do acesso"
                value={data.invoiceEmail}
                onChange={handleDataChange}
              />
              {formError.invoiceEmail && <span>{formError.invoiceEmail}</span>}
            </FormGroup>
          </Col>
        )}
      </Row>

      {needsAddress ? (
        <>
          <SectionTitle>Endereço de faturamento</SectionTitle>
          <Hint>É o endereço que vai na nota fiscal.</Hint>
          <BillingAddressFields
            value={address}
            onChange={setAddress}
            errors={{
              billingAddress: formError.billingAddress,
              billingZipCode: formError.billingZipCode,
              billingCity: formError.billingCity,
              billingState: formError.billingState,
            }}
          />
        </>
      ) : (
        <Hint>
          No plano Free o endereço de faturamento só é pedido quando você
          contratar o primeiro serviço adicional.
        </Hint>
      )}

      <ButtonsRow>
        <SecondaryButton type="button" onClick={() => setStep('plan')}>
          Voltar
        </SecondaryButton>
        <Button type="button" onClick={() => goTo('access')}>
          Continuar
        </Button>
      </ButtonsRow>
    </>
  );

  const renderAccessStep = () => (
    <>
      <form onSubmit={handleSubmit}>
        <Row>
          <Col md={12}>
            <FormGroup hasError={!!formError.email}>
              <Label htmlFor="email">
                E-mail de acesso{' '}
                <OverlayTrigger
                  delay={{ hide: 450, show: 300 }}
                  overlay={(props) => (
                    <Tooltip {...props}>
                      <b>Atenção!</b> Informe um e-mail válido e ao qual você
                      tem acesso — enviaremos um link de confirmação e você
                      precisará confirmá-lo antes de conseguir entrar na
                      plataforma.
                    </Tooltip>
                  )}
                  placement="right"
                >
                  <div>
                    <WarnIcon />
                  </div>
                </OverlayTrigger>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={access.email}
                onChange={handleAccessChange}
              />
              {formError.email && <span>{formError.email}</span>}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup hasError={!!formError.password}>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={access.password}
                onChange={handleAccessChange}
              />
              {/* Regras de criação de senha visíveis — cada item fica
                  verde quando atendido, conforme o usuário digita */}
              <PasswordRules>
                {PASSWORD_RULES.map((rule) => {
                  const ok = rule.test(access.password || '');
                  return (
                    <PasswordRuleItem key={rule.label} ok={ok}>
                      {ok ? '✓' : '•'} {rule.label}
                    </PasswordRuleItem>
                  );
                })}
              </PasswordRules>
              {formError.password && <span>{formError.password}</span>}
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup hasError={!!formError.confirmPassword}>
              <Label htmlFor="confirmPassword">Confirmar senha</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={access.confirmPassword}
                onChange={handleAccessChange}
              />
              {formError.confirmPassword && (
                <span>{formError.confirmPassword}</span>
              )}
            </FormGroup>
          </Col>
        </Row>
        {/* LGPD — aceites explícitos e individuais (art. 8º: consentimento
            por manifestação inequívoca; não pode ser texto passivo). O
            Contrato de Adesão foi aceito na etapa do plano. */}
        <div className="mt-3 mb-2">
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="acceptedTerms"
              name="acceptedTerms"
              checked={consents.acceptedTerms}
              onChange={handleConsentChange}
            />
            <label className="form-check-label" htmlFor="acceptedTerms">
              Li e aceito os{' '}
              <b style={{ cursor: 'pointer' }} onClick={toggleModalTerms}>
                Termos de Uso
              </b>
            </label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="acceptedPrivacy"
              name="acceptedPrivacy"
              checked={consents.acceptedPrivacy}
              onChange={handleConsentChange}
            />
            <label className="form-check-label" htmlFor="acceptedPrivacy">
              Li e aceito a{' '}
              <b style={{ cursor: 'pointer' }} onClick={toggleModalPrivacy}>
                Política de Privacidade
              </b>{' '}
              e autorizo o tratamento dos meus dados conforme a LGPD
            </label>
          </div>
        </div>
        <ButtonsRow>
          <SecondaryButton type="button" onClick={() => setStep('data')}>
            Voltar
          </SecondaryButton>
          <Button disabled={!consentsOk || userStatus === 'loading'}>
            Criar conta
          </Button>
        </ButtonsRow>
      </form>
      <SocialSignUpContainer>
        <SocialOrContainer>ou</SocialOrContainer>
        {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
          <GoogleSignUpButton
            payload={{ ...consents, ...profile }}
            onBlocked={socialBlocked}
          />
        )}
        <LoginSocialFacebook
          appId={process.env.REACT_APP_FACEBOOK_APP_ID || ''}
          onResolve={handleFacebookSingUp}
          onReject={() => {
            alert.error(
              'Ocorreu um erro ao tentar criar conta. Tente novamente ou entre em contato com o suporte.'
            );
          }}
        >
          <FacebookButton>
            <div>
              <FaFacebookF size={20} color="#fff" />
            </div>
            <div>Criar com Facebook</div>
          </FacebookButton>
        </LoginSocialFacebook>
      </SocialSignUpContainer>
    </>
  );

  return (
    <Container>
      <Content wide>
        <Header>
          <LogoImg src={logoImg} />
          <h3>Criar conta</h3>
        </Header>
        <Stepper>
          {STEPS.map((s, i) => (
            <StepItem key={s.id} active={i === stepIndex} done={i < stepIndex}>
              <span>{i + 1}</span>
              {s.label}
            </StepItem>
          ))}
        </Stepper>
        <Body>
          {step === 'plan' && renderPlanStep()}
          {step === 'data' && renderDataStep()}
          {step === 'access' && renderAccessStep()}
        </Body>
        {step !== 'plan' && summary}
        <Footer>
          <span>
            Já possui uma conta? <Link to={ERoutes.SignIn}>Entrar.</Link>
          </span>
        </Footer>
      </Content>
      <ModalPrivacyPolicy show={showModalPrivacy} toggle={toggleModalPrivacy} />
      <ModalUseTerms show={showModalTerms} toggle={toggleModalTerms} />
      <ModalAdhesionContract
        show={showModalAdhesion}
        toggle={toggleModalAdhesion}
      />
    </Container>
  );
};

export default SignUp;
